import { IRcsConfig, IRcsValidationErrors } from '../context/useRcsTemplateEditorContext';

const VALIDATION_KEY_HEADER_REQUIRED = 'validationHeaderRequired';
const VALIDATION_KEY_HEADER_MAX_LENGTH = 'validationHeaderMaxLength';
const VALIDATION_KEY_DESCRIPTION_REQUIRED = 'validationDescriptionRequired';
const VALIDATION_KEY_DESCRIPTION_MAX_LENGTH = 'validationDescriptionMaxLength';
const VALIDATION_KEY_AT_LEAST_ONE_ACTION = 'validationAtLeastOneActionRequired';
const VALIDATION_KEY_BUTTON_LABEL_REQUIRED = 'validationButtonLabelRequired';
const VALIDATION_KEY_BUTTON_LABEL_MAX_LENGTH = 'validationButtonLabelMaxLength';
const VALIDATION_KEY_ACTION_URL_REQUIRED = 'validationActionUrlRequired';
const VALIDATION_KEY_PHONE_NUMBER_REQUIRED = 'validationPhoneNumberRequired';

export const MAX_BUTTON_LABEL_LENGTH = 25;

/** Max length for card title when the field does not contain handlebars */
export const MAX_CARD_TITLE_LENGTH = 200;

/** Max length for description when the field does not contain handlebars */
export const MAX_DESCRIPTION_LENGTH = 2000;

/** True if the value may contain handlebars — skip static max-length validation. */
export function rcsFieldContainsHandlebars(value: string | undefined): boolean {
  return /\{\{/.test(value ?? '');
}

function isActionEmpty(action: { buttonLabel: string; actionUrl: string }): boolean {
  const label = (action.buttonLabel ?? '').trim();
  const url = (action.actionUrl ?? '').trim();
  return label.length === 0 && url.length === 0;
}

export function validateRcsConfig(config: IRcsConfig): IRcsValidationErrors {
  const errors: IRcsValidationErrors = {};
  const cardTitleRaw = config.cardTitle ?? '';
  const descriptionRaw = config.description ?? '';
  const header = cardTitleRaw.trim();
  const description = descriptionRaw.trim();

  if (header.length === 0) {
    errors.header = VALIDATION_KEY_HEADER_REQUIRED;
  } else if (!rcsFieldContainsHandlebars(cardTitleRaw) && header.length > MAX_CARD_TITLE_LENGTH) {
    errors.header = VALIDATION_KEY_HEADER_MAX_LENGTH;
  }
  if (description.length === 0) {
    errors.description = VALIDATION_KEY_DESCRIPTION_REQUIRED;
  } else if (!rcsFieldContainsHandlebars(descriptionRaw) && description.length > MAX_DESCRIPTION_LENGTH) {
    errors.description = VALIDATION_KEY_DESCRIPTION_MAX_LENGTH;
  }

  const actions = config.actions ?? [];
  const allActionsEmpty = actions.length > 0 && actions.every(isActionEmpty);
  if (allActionsEmpty) {
    errors.atLeastOneAction = VALIDATION_KEY_AT_LEAST_ONE_ACTION;
  }

  const buttonLabelByActionId: Record<string, string> = {};
  const actionUrlByActionId: Record<string, string> = {};
  actions.forEach((action, index) => {
    const label = (action.buttonLabel ?? '').trim();
    const url = (action.actionUrl ?? '').trim();
    const isEmpty = label.length === 0 && url.length === 0;
    const urlRequiredKey =
      action.actionType === 'dialPhone' ? VALIDATION_KEY_PHONE_NUMBER_REQUIRED : VALIDATION_KEY_ACTION_URL_REQUIRED;

    if (isEmpty && index > 0) {
      buttonLabelByActionId[action.id] = VALIDATION_KEY_BUTTON_LABEL_REQUIRED;
      actionUrlByActionId[action.id] = urlRequiredKey;
    } else {
      if (label.length > 0 && url.length === 0) {
        actionUrlByActionId[action.id] = urlRequiredKey;
      }
      if (label.length === 0 && url.length > 0) {
        buttonLabelByActionId[action.id] = VALIDATION_KEY_BUTTON_LABEL_REQUIRED;
      }
      if (label.length > MAX_BUTTON_LABEL_LENGTH) {
        buttonLabelByActionId[action.id] = VALIDATION_KEY_BUTTON_LABEL_MAX_LENGTH;
      }
    }
  });
  if (Object.keys(buttonLabelByActionId).length > 0) {
    errors.buttonLabelByActionId = buttonLabelByActionId;
  }
  if (Object.keys(actionUrlByActionId).length > 0) {
    errors.actionUrlByActionId = actionUrlByActionId;
  }

  return errors;
}

export function hasValidationErrors(errors: IRcsValidationErrors): boolean {
  if (errors.header ?? errors.description ?? errors.atLeastOneAction) {
    return true;
  }
  const buttonLabelById = errors.buttonLabelByActionId;
  const actionUrlById = errors.actionUrlByActionId;
  return (
    Boolean(buttonLabelById && Object.keys(buttonLabelById).length > 0) ||
    Boolean(actionUrlById && Object.keys(actionUrlById).length > 0)
  );
}
